"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import { HeroHeading } from "@/components/hero-heading";
import { PROVIDERS, DEPLOY_TARGETS } from "../_data/page-data";
import {
  AnimatedSection,
  ParallaxOrb,
  FloatingElement,
  FadeInChild,
  StaggerGrid,
} from "./motion";

/* ─── Provider icon helper ─── */
function ProviderIcon({ p }: { p: (typeof PROVIDERS)[number] }) {
  if ("circleData" in p && p.circleData) {
    return (
      <svg className="h-3.5 w-3.5" viewBox={p.viewBox}>
        <circle
          cx={p.circleData.cx}
          cy={p.circleData.cy}
          r={p.circleData.r}
          fill="#4285f4"
          opacity="0.15"
        />
        <path fill="#4285f4" d={p.pathData!} />
      </svg>
    );
  }
  return (
    <svg className="h-3.5 w-3.5" viewBox={p.viewBox} fill="currentColor">
      <path d={p.svgPath!} />
    </svg>
  );
}

export function HeroSection() {
  return (
    <AnimatedSection className="relative overflow-hidden pb-16 pt-10 md:pb-24 md:pt-16 lg:pb-32 lg:pt-24">
      {/* Decorative parallax orbs */}
      <ParallaxOrb
        speed={-0.12}
        className="-top-40 left-1/2 -translate-x-1/2 w-[900px] h-[700px] opacity-20 bg-[radial-gradient(ellipse_at_center,_rgba(99,102,241,0.4)_0%,_transparent_70%)]"
      />
      <ParallaxOrb
        speed={-0.08}
        className="top-20 -right-40 w-[500px] h-[500px] opacity-10 bg-[radial-gradient(circle,_rgba(236,72,153,0.5)_0%,_transparent_70%)]"
      />
      <ParallaxOrb
        speed={-0.1}
        className="bottom-0 -left-40 w-[400px] h-[400px] opacity-10 bg-[radial-gradient(circle,_rgba(34,197,94,0.4)_0%,_transparent_70%)]"
      />

      <div className="container relative flex max-w-[72rem] flex-col items-center gap-6 text-center">
        {/* Announcement badge */}
        <FloatingElement distance={6} duration={4}>
          <div className="group inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/80 backdrop-blur-sm px-5 py-2 text-sm font-medium text-indigo-700 shadow-sm transition-all hover:shadow-md hover:border-indigo-300">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            New: AI Website Builder + Visual Workflows + Template Marketplace
            <Icons.arrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </div>
        </FloatingElement>

        {/* Heading with ContainerTextFlip */}
        <HeroHeading />

        {/* Subheading */}
        <p className="max-w-[46rem] text-lg leading-relaxed text-muted-foreground sm:text-xl animate-fade-in-up [animation-delay:200ms]">
          Chatbots, website generation, visual workflows, human handoff, and a
          developer SDK — all in one platform. Powered by GPT, Claude & Gemini.
        </p>

        {/* CTA buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4 pt-2 animate-fade-in-up [animation-delay:400ms]">
          <Link
            href="/login"
            prefetch
            className={cn(
              buttonVariants({ size: "lg" }),
              "rounded-full px-8 shadow-lg shadow-indigo-500/20 transition-all hover:shadow-xl hover:shadow-indigo-500/30 hover:-translate-y-0.5"
            )}
          >
            <Icons.bot className="h-4 w-4 mr-2" />
            Start Building — Free
          </Link>
          <Link
            href="/docs"
            prefetch
            target="_blank"
            rel="noreferrer"
            className={cn(
              buttonVariants({ variant: "outline", size: "lg" }),
              "rounded-full px-8 bg-white/80 backdrop-blur-sm transition-all hover:-translate-y-0.5"
            )}
          >
            <Icons.plug className="h-4 w-4 mr-2" />
            API Docs
          </Link>
        </div>

        {/* Platform logos */}
        <div className="mt-8 flex flex-col items-center gap-3 animate-fade-in-up [animation-delay:600ms]">
          <span className="text-xs font-medium uppercase tracking-widest text-slate-400">
            Powered by
          </span>
          <StaggerGrid className="flex flex-wrap items-center justify-center gap-3">
            {PROVIDERS.map((p) => (
              <FadeInChild key={p.label}>
                <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200">
                  <ProviderIcon p={p} /> {p.label}
                </span>
              </FadeInChild>
            ))}
          </StaggerGrid>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-1">
            <span className="text-xs font-medium uppercase tracking-widest text-slate-400">
              Deploys to
            </span>
            <StaggerGrid className="flex items-center gap-2">
              {DEPLOY_TARGETS.map((t) => {
                const Ic = Icons[t.iconKey as keyof typeof Icons];
                return (
                  <FadeInChild key={t.label}>
                    <span
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${t.bgClass} transition-colors hover:opacity-80`}
                    >
                      <Ic className="h-3.5 w-3.5" /> {t.label}
                    </span>
                  </FadeInChild>
                );
              })}
            </StaggerGrid>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}
